import React, { useState } from "react";
import {
  Button,
  Card,
  Checkbox,
  Col,
  Input,
  InputNumber,
  List,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Tag,
  Typography
} from "antd";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { Success } from "../../../commons/Messages/Messages";
import { decimalInputProps } from "../workReports/format";
import { createTemplate, deleteTemplate, updateTemplate } from "./api";
import { typeLabels } from "./labels";

let seed = 0;
const rowKey = () => `r${Date.now().toString(36)}${(seed += 1)}`;

const TemplateEditor = ({ value, meta, onChange }) => {
  const { t, i18n } = useTranslation("dashboard");
  const types = typeLabels(t);
  const kinds = { check: t("Check"), measure: t("Measurement"), text: t("Text") };
  const setItem = (index, patch) =>
    onChange({
      ...value,
      items: value.items.map((item, i) => (i === index ? { ...item, ...patch } : item))
    });
  const move = (index, delta) => {
    const items = [...value.items];
    const [item] = items.splice(index, 1);
    items.splice(index + delta, 0, item);
    onChange({ ...value, items });
  };
  return (
    <Space direction="vertical" className="mnt-full">
      <Input
        value={value.title}
        maxLength={200}
        placeholder={t("Title")}
        onChange={event => onChange({ ...value, title: event.target.value })}
      />
      <Input
        value={value.norm}
        maxLength={300}
        placeholder={t("Standard / basis, e.g. DIN VDE 0701-0702")}
        onChange={event => onChange({ ...value, norm: event.target.value })}
      />
      <Row gutter={[8, 8]}>
        <Col xs={24} md={16}>
          <Select
            className="mnt-full"
            mode="multiple"
            value={value.installationTypes}
            placeholder={t("For installation types (empty = all)")}
            options={(meta?.installationTypes || []).map(v => ({ value: v, label: types[v] }))}
            onChange={installationTypes => onChange({ ...value, installationTypes })}
          />
        </Col>
        <Col xs={24} md={8}>
          <InputNumber
            className="mnt-full"
            min={1}
            max={120}
            precision={0}
            addonAfter={t("months")}
            value={value.defaultIntervalMonths}
            onChange={defaultIntervalMonths => onChange({ ...value, defaultIntervalMonths })}
          />
        </Col>
      </Row>
      {value.items.map((item, index) => (
        <Card key={item.rowKey} size="small" className="mnt-template-item">
          <Row gutter={[8, 8]}>
            <Col xs={24} md={8}>
              <Input
                value={item.section}
                maxLength={120}
                placeholder={t("Section")}
                onChange={event => setItem(index, { section: event.target.value })}
              />
            </Col>
            <Col xs={24} md={16}>
              <Input
                value={item.label}
                maxLength={300}
                placeholder={t("Check point")}
                onChange={event => setItem(index, { label: event.target.value })}
              />
            </Col>
            <Col xs={12} md={6}>
              <Select
                className="mnt-full"
                value={item.kind}
                options={Object.entries(kinds).map(([v, label]) => ({ value: v, label }))}
                onChange={kind => setItem(index, { kind })}
              />
            </Col>
            {item.kind === "measure" && (
              <>
                <Col xs={12} md={4}>
                  <Input
                    value={item.unit}
                    maxLength={20}
                    placeholder={t("Unit")}
                    onChange={event => setItem(index, { unit: event.target.value })}
                  />
                </Col>
                <Col xs={12} md={7}>
                  <InputNumber
                    className="mnt-full"
                    value={item.limit?.min}
                    placeholder={t("Min")}
                    {...decimalInputProps(i18n.language)}
                    onChange={min => setItem(index, { limit: { ...item.limit, min } })}
                  />
                </Col>
                <Col xs={12} md={7}>
                  <InputNumber
                    className="mnt-full"
                    value={item.limit?.max}
                    placeholder={t("Max")}
                    {...decimalInputProps(i18n.language)}
                    onChange={max => setItem(index, { limit: { ...item.limit, max } })}
                  />
                </Col>
              </>
            )}
            <Col xs={24}>
              <Input
                value={item.hint}
                maxLength={500}
                placeholder={t("Hint for the technician")}
                onChange={event => setItem(index, { hint: event.target.value })}
              />
            </Col>
            <Col xs={24} className="mnt-template-actions">
              <Checkbox
                checked={item.required}
                onChange={event => setItem(index, { required: event.target.checked })}
              >
                {t("Required")}
              </Checkbox>
              <Space>
                <Button
                  size="small"
                  aria-label={t("Move up")}
                  icon={<ArrowUpOutlined />}
                  disabled={index === 0}
                  onClick={() => move(index, -1)}
                />
                <Button
                  size="small"
                  aria-label={t("Move down")}
                  icon={<ArrowDownOutlined />}
                  disabled={index === value.items.length - 1}
                  onClick={() => move(index, 1)}
                />
                <Button
                  size="small"
                  danger
                  aria-label={t("Remove")}
                  icon={<DeleteOutlined />}
                  onClick={() =>
                    onChange({ ...value, items: value.items.filter((_, i) => i !== index) })
                  }
                />
              </Space>
            </Col>
          </Row>
        </Card>
      ))}
      <Button
        icon={<PlusOutlined />}
        onClick={() =>
          onChange({
            ...value,
            items: [
              ...value.items,
              {
                rowKey: rowKey(),
                key: "",
                section: value.items[value.items.length - 1]?.section || "",
                label: "",
                kind: "check",
                unit: "",
                limit: { min: null, max: null },
                hint: "",
                required: true
              }
            ]
          })
        }
      >
        {t("Add check point")}
      </Button>
    </Space>
  );
};

/** Built-in checklists (copy to adapt) and the company's own. */
const TemplatesModal = ({ open, meta, onClose, onChanged }) => {
  const { t } = useTranslation("dashboard");
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const canEdit = meta?.can.update;

  const startEdit = (template, copy) =>
    setEditing({
      id: copy ? null : template.key,
      title: copy ? `${template.title} (${t("own copy")})` : template.title,
      norm: template.norm || "",
      installationTypes: template.installationTypes || [],
      defaultIntervalMonths: template.defaultIntervalMonths || 12,
      basedOn: copy ? template.key : template.basedOn || "",
      items: template.items.map(item => ({ ...item, rowKey: rowKey() }))
    });

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        ...editing,
        items: editing.items.map(item => {
          const copy = { ...item };
          delete copy.rowKey;
          return copy;
        })
      };
      if (editing.id) await updateTemplate(editing.id, body);
      else await createTemplate(body);
      Success(t("Saved"));
      setEditing(null);
      onChanged();
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setBusy(false);
    }
  };

  const remove = async template => {
    try {
      await deleteTemplate(template.key);
      onChanged();
    } catch (error) {
      generalErrorAlert(error);
    }
  };

  return (
    <Modal
      rootClassName="maintenance-page"
      open={open}
      width={860}
      title={editing ? t("Edit checklist") : t("Checklists")}
      onCancel={editing ? () => setEditing(null) : onClose}
      footer={
        editing
          ? [
              <Button key="back" onClick={() => setEditing(null)}>
                {t("Back")}
              </Button>,
              <Button key="save" type="primary" loading={busy} onClick={save}>
                {t("Save")}
              </Button>
            ]
          : [
              <Button key="close" onClick={onClose}>
                {t("Close")}
              </Button>
            ]
      }
    >
      {editing ? (
        <TemplateEditor value={editing} meta={meta} onChange={setEditing} />
      ) : (
        <>
          <Typography.Paragraph type="secondary">
            {t(
              "Built-in checklists contain the usual limit values. Copy one to adapt checks or limits to your devices and the manufacturer's data."
            )}
          </Typography.Paragraph>
          <List
            dataSource={meta?.templates || []}
            renderItem={template => (
              <List.Item
                actions={
                  canEdit
                    ? template.builtIn
                      ? [
                          <Button
                            key="copy"
                            icon={<CopyOutlined />}
                            onClick={() => startEdit(template, true)}
                          >
                            {t("Copy")}
                          </Button>
                        ]
                      : [
                          <Button
                            key="edit"
                            aria-label={t("Edit")}
                            icon={<EditOutlined />}
                            onClick={() => startEdit(template, false)}
                          />,
                          <Popconfirm
                            key="del"
                            title={t("Delete this checklist?")}
                            onConfirm={() => remove(template)}
                          >
                            <Button danger aria-label={t("Delete")} icon={<DeleteOutlined />} />
                          </Popconfirm>
                        ]
                    : []
                }
              >
                <List.Item.Meta
                  title={
                    <Space wrap>
                      <span>{template.title}</span>
                      <Tag>{template.builtIn ? t("Built-in") : t("Own")}</Tag>
                    </Space>
                  }
                  description={[
                    template.norm,
                    t("{{n}} check points", { n: template.items.length })
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                />
              </List.Item>
            )}
          />
        </>
      )}
    </Modal>
  );
};

export default TemplatesModal;
